import React, { FC, useCallback, useMemo } from 'react';

import classNames from 'classnames';

import { EvMainTitle, EvSection, EvTextInput } from '@evinced-private/ui-common';

import { isPropertyInListMode } from 'src/helpers/PropertySourceHelper';
import { SETTINGS_SECTION_TYPES } from 'src/types/SettingsSectionTypes';

import { PropertyJsonEditor } from '../../../components/property-settings/property-json-editor/PropertyJsonEditor';
import { PropertySettingsSourcePicker } from '../../../components/property-settings/source-picker/PropertySettingsSourcePicker';
import { usePropertySettings } from '../context';
import { setDraft, setLoading as setLoadingAction, setName } from '../state';

import { TITLE_LABELS } from './PropertySettingsTitleV2.labels';

export const PropertySettingsTitleV2: FC = () => {
	const { state, dispatch, isListMode, isNew, propertyId, setSourceMode, save, goToProperty } =
		usePropertySettings();

	const { draft: property, original: originalProperty, sourceMode, errors } = state;
	const nameError = errors.find((e) => e.sectionId === SETTINGS_SECTION_TYPES.NAME);

	const onNameChange = useCallback(
		(name: string) => {
			dispatch(setName(name));
		},
		[dispatch]
	);

	const isInListMode = useMemo(() => isPropertyInListMode(sourceMode), [sourceMode]);

	const title = isNew ? TITLE_LABELS.NEW_PROPERTY_TITLE : TITLE_LABELS.EDIT_PROPERTY_TITLE;
	const classes = classNames('page-heading', { 'new-property': isNew });

	return (
		<div className="property-settings-title">
			<EvMainTitle className={classes}>{title}</EvMainTitle>
			<EvSection
				className="property-title"
				ariaLabel={TITLE_LABELS.SECTION_ARIA_LABEL}
				skipLinkId={TITLE_LABELS.SKIP_LINK_ID}
			>
				<div className="property-name">
					<div className="new-label">{TITLE_LABELS.PROPERTY_NAME_LABEL}</div>
					<div className="name-input-container">
						<EvTextInput
							ariaLabel={TITLE_LABELS.PROPERTY_NAME_LABEL}
							error={(nameError && nameError.message) || ''}
							value={property.name}
							onChange={onNameChange}
						/>
					</div>
				</div>
				<div className="spacer" />
				<PropertySettingsSourcePicker
					property={property}
					originalProperty={originalProperty}
					sourceMode={sourceMode}
					getPropertyData={(s) => setSourceMode(property, s)}
					setPropertySourceMode={(s) => {
						setSourceMode(property, s);
					}}
					setLoading={(l) => dispatch(setLoadingAction(l))}
				/>
				{!isInListMode && (
					<PropertyJsonEditor
						propertyId={propertyId}
						goToProperty={goToProperty}
						editProperty={(p, shouldCrawl) => {
							dispatch(setDraft(p));
							save({ draft: p, isListMode, shouldCrawl });
						}}
					/>
				)}
			</EvSection>
		</div>
	);
};
